import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HttpService } from '../../game/services/HttpService';
import './Settings.component.css';
import logo from '../../assets/compressed/logoMainH-min.png';

interface UserProfile {
  id?: string;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const httpService = new HttpService();
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    if (!token || !userId) {
      navigate('/login');
      return;
    }
    httpService.setToken(token);
    loadProfile();
  }, [navigate, token, userId]);

  const loadProfile = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await httpService.get<any>('user/me');
      if (response?.status === 'success' && response?.data) {
        const user = response.data as UserProfile;
        setProfile(user);
        setFirstName(user.firstName ?? '');
        setLastName(user.lastName ?? '');
      } else {
        setError(response?.message || 'Failed to load profile');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setSaving(true);
    try {
      const response = await httpService.patch<any>('user/me', {
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
      });
      if (response?.status === 'success') {
        const displayName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ') || 'Player';
        localStorage.setItem('username', displayName);
        setSuccessMessage('Profile updated successfully.');
        setProfile(response?.data ?? profile);
      } else {
        setError(response?.message || 'Failed to update profile');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!token || !userId) {
    return null;
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div className="settings-title">
          <img className="header-logo" src={logo} alt="Wild Chips Poker" onClick={() => navigate('/dashboard')} />
          <h1>Settings</h1>
        </div>
        <button type="button" className="btn-secondary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>

      <div className="settings-content">
        {loading ? (
          <p className="settings-loading">Loading profile...</p>
        ) : (
          <div className="settings-card">
            {error && <div className="settings-error">{error}</div>}
            {successMessage && <div className="settings-success">{successMessage}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={profile?.email ?? ''}
                  readOnly
                  disabled
                  className="settings-email-input"
                  aria-readonly
                />
                <p className="form-hint">Email cannot be changed.</p>
              </div>

              <div className="form-group">
                <label>First name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  placeholder="First name"
                />
              </div>

              <div className="form-group">
                <label>Last name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  placeholder="Last name"
                />
              </div>

              <button type="submit" className="btn-primary settings-save" disabled={saving}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
